import {
  CLINICAL_STAGE_LABEL,
  COACH_BMI_MISSING_TEXT,
  COACH_MEASUREMENT_SOURCE_LABEL,
  SUBSCRIPTION_PLAN_LABEL,
  coachBmi,
  type CoachPatientHeader,
  type CoachReading,
} from "@care-by-ella/contracts";
import { treatmentStateLabel } from "./state-labels";

// TES-1074 point 1 — the coach header's cells, as text.
//
// Pure so the rules can be tested without a DOM: every cell has a value even
// when the fact is missing, and the missing state says what is missing
// (A-01: "Om ett värde saknas ska det visas uttryckligen").

export interface CoachHeaderCell {
  key: string;
  label: string;
  value: string;
  detail: string | null;
  missing: boolean;
  /**
   * TES-1333 — a technical status shown as a small badge beside the value,
   * only where the value alone does not say it. Never the cell's headline.
   */
  badge: string | null;
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function fmtNumber(n: number): string {
  return n.toLocaleString("sv-SE", { maximumFractionDigits: 1 });
}

function readingDetail(r: CoachReading): string {
  return `${fmtDay(r.measuredAt)} · ${COACH_MEASUREMENT_SOURCE_LABEL[r.source]}`;
}

// A-01: "BMI är orienterande coachkontext och får inte användas av coachen för medicinsk bedömning eller behandlingsbehörighet."
export const COACH_BMI_CAVEAT =
  "Orienterande coachkontext – inte för medicinsk bedömning";

export function coachHeaderCells(h: CoachPatientHeader): CoachHeaderCell[] {
  const bmi = coachBmi({
    weightKg: h.weight?.value,
    heightCm: h.height?.value,
  });
  const cells: CoachHeaderCell[] = [
    h.weight
      ? {
          key: "weight",
          label: "Senaste vikt",
          value: `${fmtNumber(h.weight.value)} kg`,
          detail: readingDetail(h.weight),
          missing: false,
          badge: null,
        }
      : {
          key: "weight",
          label: "Senaste vikt",
          value: "Saknas",
          detail: "Ingen vikt registrerad",
          missing: true,
          badge: null,
        },
    h.height
      ? {
          key: "height",
          label: "Längd",
          value: `${fmtNumber(h.height.value)} cm`,
          detail: readingDetail(h.height),
          missing: false,
          badge: null,
        }
      : {
          key: "height",
          label: "Längd",
          value: "Saknas",
          detail: "Ingen längd registrerad",
          missing: true,
          badge: null,
        },
    bmi.value !== null
      ? {
          key: "bmi",
          label: "BMI",
          value: fmtNumber(bmi.value),
          detail: COACH_BMI_CAVEAT,
          missing: false,
          badge: null,
        }
      : {
          key: "bmi",
          label: "BMI",
          value: "Saknas",
          detail: COACH_BMI_MISSING_TEXT[bmi.missing],
          missing: true,
          badge: null,
        },
    h.goalWeightKg !== null
      ? {
          key: "goal",
          label: "Målvikt",
          value: `${fmtNumber(h.goalWeightKg)} kg`,
          detail: "Registrerad målvikt",
          missing: false,
          badge: null,
        }
      : {
          key: "goal",
          label: "Målvikt",
          value: "Saknas",
          detail: "Ingen målvikt registrerad",
          missing: true,
          badge: null,
        },
    {
      key: "phase",
      label: "Klinisk fas",
      value: CLINICAL_STAGE_LABEL[h.clinicalStage],
      detail: null,
      missing: false,
      badge: null,
    },
    treatmentCell(h),
    subscriptionCell(h),
    coachCell(h),
  ];
  return cells;
}

// TES-1333 — A-01, "SPECIFIK INFORMATION FÖRE STATUSORDET AKTIV": "I coach-admin får ordet ”Aktiv” aldrig vara kortets eller sektionens huvudsakliga informationsbärare."
// The three status cells below lead with the concrete fact (treatment month,
// plan, what the coach access rests on) and keep "Aktiv" as a small badge,
// only where the value does not already say it. A cell that is NOT active
// (paused, ended, no subscription, no coach access) keeps the status as its
// value: there the status is the information that changes her decision.

function treatmentCell(h: CoachPatientHeader): CoachHeaderCell {
  const base = { key: "treatment", label: "Behandling" } as const;
  if (!h.treatment.state) {
    return {
      ...base,
      value: "Ingen behandling påbörjad",
      detail: "Ingen behandlingsmånad pågår",
      missing: true,
      badge: null,
    };
  }
  if (h.treatment.state === "active" && h.treatment.month !== null) {
    return {
      ...base,
      value: `Behandlingsmånad ${h.treatment.month}`,
      detail: null,
      missing: false,
      // The phase cell beside it already reads "Aktiv behandling". A-01:
      // "Statusen får inte upprepas under flera rubriker när den inte förändrar coachens beslut."
      badge:
        h.clinicalStage === "active_treatment"
          ? null
          : treatmentStateLabel(h.treatment.state),
    };
  }
  return {
    ...base,
    value: treatmentStateLabel(h.treatment.state),
    detail:
      h.treatment.month !== null
        ? `Behandlingsmånad ${h.treatment.month}`
        : "Ingen behandlingsmånad pågår",
    missing: h.treatment.month === null,
    badge: null,
  };
}

function subscriptionCell(h: CoachPatientHeader): CoachHeaderCell {
  const base = { key: "subscription", label: "Abonnemang" } as const;
  const plan = h.subscription.plan
    ? SUBSCRIPTION_PLAN_LABEL[h.subscription.plan]
    : null;
  if (!h.subscription.active) {
    return {
      ...base,
      value: "Inte aktivt",
      detail: plan ?? "Plan inte registrerad",
      missing: false,
      badge: null,
    };
  }
  // The plan name alone does not say the subscription is running, so
  // "Aktivt" stays beside it as a badge.
  return {
    ...base,
    value: plan ?? "Plan inte registrerad",
    detail: null,
    missing: plan === null,
    badge: "Aktivt",
  };
}

function coachCell(h: CoachPatientHeader): CoachHeaderCell {
  const base = { key: "coach", label: "Coachbehörighet" } as const;
  // TES-1121 (Ö-342 a) — before her first subscription access is inactive,
  // and the detail says when it opens rather than the "Underhåll"
  // (maintenance plan) line about a readable earlier chat she never had.
  if (h.coachAccess === "inactive") {
    return {
      ...base,
      value: "Inaktiv",
      detail: h.coachOpensWithSubscription
        ? "Öppnar när abonnemanget startar"
        : "Ingen ny coachkontakt – tidigare chatt är läsbar",
      missing: false,
      badge: null,
    };
  }
  // Active: what the access rests on is the information, and it already
  // says the access is there, so no badge.
  //
  // TES-1118 — coach access is read from the plan alone, and a patient with
  // no subscription has no plan, which reads as active (coach-access.ts). So
  // "included in the subscription" was shown next to "Abonnemang: Inte
  // aktivt". The text never claims a subscription she does not have.
  return h.subscription.active
    ? {
        ...base,
        value: "Coachen ingår i abonnemanget",
        detail: null,
        missing: false,
        badge: null,
      }
    : {
        ...base,
        value: "Coachchatten är öppen",
        detail: "Inget aktivt abonnemang",
        missing: false,
        badge: null,
      };
}
